/**
 * A qualidade da IA (etapa C; guia 6.3 e 15.1): o que a tela conta e escreve,
 * sem rede, para o teste alcançar.
 */

export const ASSUNTOS_DA_IA = ['equipamento', 'ordem', 'painel', 'plano', 'aviso', 'cupom', 'folha', 'folha_de_turno'] as const;
export type AssuntoDaIa = (typeof ASSUNTOS_DA_IA)[number];

/** O nome de cada saída da IA, como quem usa o sistema a chama. */
export const ROTULO_DO_ASSUNTO: Readonly<Record<AssuntoDaIa, string>> = {
  equipamento: 'Pergunta sobre a máquina',
  ordem: 'Hipótese e revisão da O.S.',
  painel: 'Resumo do painel',
  plano: 'Rascunho de plano',
  aviso: 'Explicação de aviso',
  cupom: 'Leitura do cupom do posto',
  folha: 'Leitura da folha de abastecimento',
  folha_de_turno: 'Leitura da folha de turno',
};

export const PERIODOS = [7, 30, 90] as const;
export type Periodo = (typeof PERIODOS)[number];

export interface BuscaDaQualidade {
  /** A aba de seção: o registro (padrão) ou o conjunto de avaliação. */
  readonly aba?: 'avaliacao';
  readonly dias?: Periodo;
  readonly assunto?: AssuntoDaIa;
  readonly pagina?: number;
}

/** O filtro no endereço (p.16): o padrão (30 dias, todos, página 1) não aparece. */
export function validarBuscaDaQualidade(entrada: Record<string, unknown>): BuscaDaQualidade {
  const dias = Number(entrada['dias']);
  const assunto = entrada['assunto'];
  const pagina = Number(entrada['pagina']);
  return {
    ...(entrada['aba'] === 'avaliacao' ? { aba: 'avaliacao' as const } : {}),
    ...((PERIODOS as readonly number[]).includes(dias) && dias !== 30 ? { dias: dias as Periodo } : {}),
    ...(typeof assunto === 'string' && (ASSUNTOS_DA_IA as readonly string[]).includes(assunto) ? { assunto: assunto as AssuntoDaIa } : {}),
    ...(Number.isInteger(pagina) && pagina > 1 ? { pagina } : {}),
  };
}

export interface LinhaPorAssunto {
  readonly assunto: string;
  readonly respostas: number;
  readonly sabe: number;
  readonly comCitacao: number;
  readonly naoSei: number;
  readonly tokensEntrada: number;
  readonly tokensSaida: number;
  readonly avaliadas: number;
  readonly uteis: number;
}

export interface LeituraDeFolhaNoPeriodo {
  readonly origem: string;
  readonly lidas: number;
  readonly conferidas: number;
  readonly camposLidos: number;
  readonly camposCorrigidos: number;
}

export interface Totais {
  readonly respostas: number;
  readonly naoSei: number;
  /** Fundamentação (6.3): das respostas "sei", as que citam. `null` sem resposta "sei". */
  readonly fundamentacao: number | null;
  /** Utilidade (6.3): das avaliadas, as que serviram. `null` sem nota. */
  readonly utilidade: number | null;
  readonly avaliadas: number;
  /** O acerto da leitura de folha: campos lidos que a conferência não corrigiu. `null` sem conferência. */
  readonly acertoDaLeitura: number | null;
  readonly camposLidos: number;
  readonly tokens: number;
}

const taxa = (parte: number, todo: number): number | null => (todo > 0 ? parte / todo : null);

export function totais(porAssunto: readonly LinhaPorAssunto[], folhas: readonly LeituraDeFolhaNoPeriodo[]): Totais {
  const soma = (f: (l: LinhaPorAssunto) => number) => porAssunto.reduce((t, l) => t + f(l), 0);
  /* O cupom e a explicacao contam "sabe" sem citação de documento por natureza
     (o cupom não cita; o aviso cita os fatos dele). A fundamentação do guia é
     da RESPOSTA TÉCNICA: só os assuntos que citam trecho entram na conta. */
  const tecnicas = porAssunto.filter((l) => l.assunto !== 'cupom');
  const sabe = tecnicas.reduce((t, l) => t + l.sabe, 0);
  const comCitacao = tecnicas.reduce((t, l) => t + l.comCitacao, 0);
  const camposLidos = folhas.reduce((t, f) => t + f.camposLidos, 0);
  const corrigidos = folhas.reduce((t, f) => t + f.camposCorrigidos, 0);
  return {
    respostas: soma((l) => l.respostas),
    naoSei: soma((l) => l.naoSei),
    fundamentacao: taxa(comCitacao, sabe),
    utilidade: taxa(soma((l) => l.uteis), soma((l) => l.avaliadas)),
    avaliadas: soma((l) => l.avaliadas),
    acertoDaLeitura: taxa(camposLidos - corrigidos, camposLidos),
    camposLidos,
    tokens: soma((l) => l.tokensEntrada + l.tokensSaida),
  };
}

/** `0,8` -> `80%`, sem casa decimal: a meta do guia é escrita em inteiro. */
export function porcento(valor: number): string {
  return `${Math.round(valor * 100).toLocaleString('pt-BR')}%`;
}

/** O placar de uma rodada do conjunto de avaliação (guia 15.1), como o servidor devolve. */
export interface Placar {
  readonly casos: number;
  readonly precisao: number | null;
  readonly abstencao: number | null;
  readonly recuperacao: number | null;
  readonly fundamentacao: number | null;
  readonly violacoes: number;
}

/** Uma taxa do placar, ou o porquê de não ter: nenhum caso da rodada mede aquela dimensão. */
export const taxaOuNada = (v: number | null): string => (v === null ? 'Sem caso' : porcento(v));
